import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { HeroSection } from "@/components/HeroSection";
import { Footer } from "@/components/Footer";
import { ShelterMap } from "@/components/ShelterMap";
import { ShelterList } from "@/components/ShelterList";
import { ShelterListSkeleton } from "@/components/ShelterListSkeleton";
import { ShelterFiltersComponent } from "@/components/ShelterFilters";
import { ShelterDetail } from "@/components/ShelterDetail";
import { LocationBlockedInfo } from "@/components/LocationBlockedInfo";
import { useShelters } from "@/hooks/useShelters";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useSectionTracking } from "@/hooks/useSectionTracking";
import type { Shelter, ShelterFilters } from "@/types/shelter";
import { isUrgentNeedsActive } from "@/types/shelter";
import { X, Locate, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { includesNormalized } from "@/lib/geoText";
import { ScrollProgressBar } from "@/components/ScrollProgressBar";
import { trackEvent } from "@/hooks/useEventTracking";

// Calculate distance in km between two points
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export interface ShelterWithDistance extends Shelter {
  distance?: number;
}

const Index = () => {
  const { data: shelters, isLoading, error } = useShelters();
  const { hasConsent } = useCookieConsent();
  const [selectedShelter, setSelectedShelter] = useState<Shelter | null>(null);
  const [selectedShelterSource, setSelectedShelterSource] = useState<string>("unknown");
  const [showDetail, setShowDetail] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationDismissed, setLocationDismissed] = useState(false);
  const [locationBlocked, setLocationBlocked] = useState<{ show: boolean; isIOS: boolean }>({ show: false, isIOS: false });
  const [scrollPosition, setScrollPosition] = useState({ atTop: true, atBottom: false });
  const sidebarRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  // Section tracking
  const heroRef = useSectionTracking("Hero");
  const filtersRef = useSectionTracking("Filtry");
  const mapSectionRef = useSectionTracking("Mapa", 0.2);
  const listRef = useSectionTracking("Lista schronisk", 0.2);
  const footerRef = useSectionTracking("Stopka");

  const handleScrollToMap = useCallback(() => {
    mapRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  const [filters, setFilters] = useState<ShelterFilters>({
    searchQuery: "",
    dogWalking: false,
    volunteering: false,
    urgentNeeds: false,
    dogsAvailable: false,
    catsAvailable: false,
    organizationVerified: false,
    city: "",
  });

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.searchQuery !== "" ||
      filters.dogWalking ||
      filters.volunteering ||
      filters.urgentNeeds ||
      filters.dogsAvailable ||
      filters.catsAvailable ||
      filters.organizationVerified ||
      filters.city !== "";
  }, [filters]);

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      searchQuery: "",
      dogWalking: false,
      volunteering: false,
      urgentNeeds: false,
      dogsAvailable: false,
      catsAvailable: false,
      organizationVerified: false,
      city: "",
    });
  };

  // Get unique cities for filter dropdown
  const cities = useMemo(() => {
    if (!shelters) return [];
    const uniqueCities = [...new Set(shelters.map((s) => s.city))];
    return uniqueCities.sort();
  }, [shelters]);

  // Helper function to check if shelter has exact location
  const hasExactLocation = (shelter: Shelter): boolean => {
    if (!shelter.address) return false;
    const addressLower = shelter.address.toLowerCase().trim();
    const cityLower = shelter.city.toLowerCase().trim();
    if (addressLower === '' || addressLower === cityLower) return false;
    if (addressLower === `${cityLower}, polska` || addressLower === `${cityLower}, poland`) return false;
    return true;
  };

  // Filter and sort shelters based on active filters and user location
  const filteredShelters = useMemo((): ShelterWithDistance[] => {
    if (!shelters) return [];
    
    let result = shelters.filter((shelter) => {
      // Search query filter - use normalized comparison for Polish diacritics
      if (filters.searchQuery) {
        const matchesName = includesNormalized(shelter.name, filters.searchQuery);
        const matchesCity = includesNormalized(shelter.city, filters.searchQuery);
        if (!matchesName && !matchesCity) return false;
      }
      
      if (filters.dogWalking && shelter.dog_walking_available !== true) return false;
      if (filters.volunteering && shelter.volunteering_available !== true) return false;
      if (filters.urgentNeeds && !isUrgentNeedsActive(shelter)) return false;
      if (filters.dogsAvailable && shelter.dogs_available !== true) return false;
      if (filters.catsAvailable && shelter.cats_available !== true) return false;
      if (filters.organizationVerified && shelter.organization_verified !== true) return false;
      if (filters.city && shelter.city !== filters.city) return false;
      return true;
    });

    // Add distance if user location is available
    if (userLocation) {
      result = result.map(shelter => ({
        ...shelter,
        distance: calculateDistance(userLocation[0], userLocation[1], shelter.lat, shelter.lng)
      }));
    }

    // Sort: shelters with address first, then by distance (if available) or name
    result.sort((a: ShelterWithDistance, b: ShelterWithDistance) => {
      const aHasAddress = hasExactLocation(a);
      const bHasAddress = hasExactLocation(b);
      
      // Shelters without address go to the end
      if (aHasAddress && !bHasAddress) return -1;
      if (!aHasAddress && bHasAddress) return 1;
      
      // If both have/don't have address, sort by distance or name
      if (userLocation && a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      
      return a.name.localeCompare(b.name, 'pl');
    });

    return result;
  }, [shelters, filters, userLocation]);

  // Track empty search results (debounced)
  useEffect(() => {
    if (filters.searchQuery.length >= 3 && filteredShelters.length === 0) {
      const timeout = setTimeout(() => {
        trackEvent("Wyszukiwanie bez wyników", { query: filters.searchQuery });
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [filters.searchQuery, filteredShelters.length]);

  // Track scroll position for fade effects
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = sidebar;
      setScrollPosition({
        atTop: scrollTop < 10,
        atBottom: scrollTop + clientHeight >= scrollHeight - 10
      });
    };

    sidebar.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => sidebar.removeEventListener('scroll', handleScroll);
  }, [filteredShelters]);

  const handleShelterSelect = useCallback((shelter: Shelter, source: string = "unknown") => {
    setSelectedShelter(shelter);
    setSelectedShelterSource(source);
    setShowDetail(true);
  }, []);

  const handleShelterSelectFromList = useCallback((shelter: Shelter) => handleShelterSelect(shelter, "list"), [handleShelterSelect]);
  const handleShelterSelectFromMap = useCallback((shelter: Shelter) => handleShelterSelect(shelter, "map"), [handleShelterSelect]);
  const handleShelterSelectFromSearch = useCallback((shelter: Shelter) => handleShelterSelect(shelter, "search"), [handleShelterSelect]);

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedShelter(null);
  };

  const handleClearSelection = () => {
    setSelectedShelter(null);
  };

  // Handle Escape key to close fullscreen map
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMapFullscreen) {
        setIsMapFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMapFullscreen]);

  const handleUserLocationChange = (location: [number, number] | null) => {
    setUserLocation(location);
  };

  const handleLocateUser = () => {
    if (!hasConsent) {
      toast.error("Musisz zaakceptować cookies, aby korzystać z geolokalizacji");
      return;
    }

    if (!navigator.geolocation) {
      toast.error("Twoja przeglądarka nie obsługuje geolokalizacji");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        if (latitude < 48.5 || latitude > 55.5 || longitude < 13.5 || longitude > 24.5) {
          toast.error("Twoja lokalizacja jest poza granicami Polski");
          setIsLocating(false);
          return;
        }

        setUserLocation([latitude, longitude]);
        setLocationDismissed(true);
        toast.success("Lista posortowana wg odległości od Ciebie!");
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        const ua = navigator.userAgent;
        const isIOS =
          /iPad|iPhone|iPod/.test(ua) ||
          (ua.includes("Macintosh") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            // Show the nice inline popup instead of toast
            setLocationBlocked({ show: true, isIOS });
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Nie udało się określić lokalizacji");
            break;
          default:
            toast.error("Błąd podczas pobierania lokalizacji");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        
        {/* Skeleton Filters */}
        <div className="sticky top-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border">
          <div className="max-w-screen-2xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3 overflow-x-auto">
              <Skeleton className="h-9 w-48 rounded-lg flex-shrink-0" />
              <Skeleton className="h-8 w-24 rounded-full flex-shrink-0" />
              <Skeleton className="h-8 w-20 rounded-full flex-shrink-0" />
              <Skeleton className="h-8 w-28 rounded-full flex-shrink-0" />
            </div>
          </div>
        </div>
        
        <main className="flex-1 flex flex-col lg:flex-row">
          {/* Skeleton Map */}
          <div className="w-full lg:w-1/2 h-[35vh] min-h-[250px] lg:h-[calc(100vh-120px)] lg:sticky lg:top-0 lg:order-2">
            <Skeleton className="w-full h-full" />
          </div>
          
          {/* Skeleton List */}
          <aside className="w-full lg:w-1/2 bg-muted/30 border-t lg:border-t-0 lg:border-r border-border flex-1 lg:order-1 lg:h-[calc(100vh-120px)]">
            <div className="p-4">
              <Skeleton className="h-4 w-36 mb-4" />
              <ShelterListSkeleton count={4} />
            </div>
          </aside>
        </main>
        
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <p className="text-destructive font-medium mb-2">Błąd ładowania danych</p>
          <p className="text-muted-foreground text-sm">Spróbuj odświeżyć stronę</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>GdzieSchronisko - Mapa Schronisk dla Zwierząt w Polsce</title>
        <meta name="description" content="Interaktywna mapa schronisk dla zwierząt w Polsce. Znajdź najbliższe schronisko, sprawdź aktualne potrzeby, możliwości wolontariatu, spacerów z psami i adopcji." />
        <link rel="canonical" href="https://gdzieschronisko.pl/" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <div ref={heroRef}>
        <HeroSection shelters={shelters || []} onScrollToMap={handleScrollToMap} onShelterSelect={handleShelterSelectFromSearch} />
      </div>
      
      {/* Filters Bar */}
      <div ref={(node) => { (mapRef as React.MutableRefObject<HTMLDivElement | null>).current = node; (filtersRef as React.MutableRefObject<HTMLDivElement | null>).current = node; }}>
        <ShelterFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        cities={cities}
        resultsCount={filteredShelters.length}
        totalCount={shelters?.length || 0}
        shelters={shelters || []}
        />
      </div>
      
      <main className="flex-1 flex flex-col lg:flex-row">
        {/* Map - always on top on mobile, right side on desktop */}
        <div ref={mapSectionRef} className="w-full lg:w-1/2 h-[35vh] min-h-[250px] lg:h-[calc(100vh-120px)] lg:sticky lg:top-0 lg:order-2">
          <ShelterMap
            shelters={filteredShelters}
            selectedShelterId={selectedShelter?.id ?? null}
            onShelterSelect={handleShelterSelectFromMap}
            onClearSelection={handleClearSelection}
            userLocation={userLocation}
            onUserLocationChange={handleUserLocationChange}
            isFullscreen={isMapFullscreen}
            onToggleFullscreen={() => setIsMapFullscreen(true)}
            focusCity={filters.city}
          />
        </div>

        {/* Sidebar - List (below map on mobile, left side on desktop) */}
        <aside ref={listRef} className="relative w-full lg:w-1/2 border-t lg:border-t-0 lg:border-r border-border flex-1 lg:order-1 lg:h-[calc(100vh-120px)] lg:flex lg:flex-row">
          {/* Left gutter - allows page scroll on desktop, matches filter bar color */}
          <div className="hidden lg:flex w-2.5 min-w-[10px] flex-shrink-0 cursor-default bg-muted/30" />
          
          {/* Scrollable content area */}
          <div className="relative flex-1 h-full bg-muted/30 min-w-0">
            {/* Scroll progress bar */}
            <ScrollProgressBar containerRef={sidebarRef as React.RefObject<HTMLElement>} />
            
            {/* Top fade gradient */}
            <div 
              className={`hidden lg:block absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-muted/30 to-transparent z-10 pointer-events-none transition-opacity duration-300 ${scrollPosition.atTop ? 'opacity-0' : 'opacity-100'}`}
            />
            
            <div ref={sidebarRef} className="h-full lg:overflow-y-auto p-4 custom-scrollbar">
              {/* Location blocked info popup */}
              {locationBlocked.show && (
                <div className="mb-4">
                  <LocationBlockedInfo
                    isIOS={locationBlocked.isIOS}
                    onClose={() => {
                      setLocationBlocked({ show: false, isIOS: false });
                      setLocationDismissed(true);
                    }}
                  />
                </div>
              )}

              {/* Location prompt - show only if location not set, not dismissed, and not blocked */}
              {!userLocation && !locationDismissed && !locationBlocked.show && (
                <div className="mb-3 flex items-center gap-2 text-sm">
                  <Locate className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-foreground">
                    Znajdź schroniska w pobliżu -{" "}
                    <button
                      onClick={handleLocateUser}
                      disabled={isLocating || !hasConsent}
                      className="text-primary hover:text-primary/80 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLocating ? "szukam..." : "użyj lokalizacji"}
                    </button>
                  </span>
                  <button
                    onClick={() => setLocationDismissed(true)}
                    className="p-1 hover:bg-muted rounded text-muted-foreground ml-auto"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Community banner */}
               <div className="mb-4 p-2 sm:p-2.5 bg-accent/60 border border-primary/15 rounded-xl">
                <p className="text-[11px] sm:text-xs text-center text-accent-foreground">
                  🐾 <strong>Budujemy to razem!</strong> Każdy może aktualizować potrzeby schronisk. Masz świeże info? Kliknij na schronisko i zgłoś aktualizację.
                </p>
              </div>

              {/* Results count */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">
                  Znaleziono {filteredShelters.length} {
                    filteredShelters.length === 1 ? 'schronisko' :
                    filteredShelters.length < 5 ? 'schroniska' : 'schronisk'
                  }
                  {userLocation && ' (wg odległości)'}
                </span>
              </div>
              
              <ShelterList
                shelters={filteredShelters}
                selectedShelterId={selectedShelter?.id ?? null}
                onShelterSelect={handleShelterSelectFromList}
                containerRef={sidebarRef}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>
            
            {/* Bottom fade gradient */}
            <div 
              className={`hidden lg:block absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/30 to-transparent z-10 pointer-events-none transition-opacity duration-300 ${scrollPosition.atBottom ? 'opacity-0' : 'opacity-100'}`}
            />
          </div>
        </aside>
      </main>

      {/* Fullscreen Map Modal */}
      {isMapFullscreen && (
        <div className="fixed inset-0 z-[1000] bg-background">
          <ShelterMap
            shelters={filteredShelters}
            selectedShelterId={selectedShelter?.id ?? null}
            onShelterSelect={handleShelterSelectFromMap}
            onClearSelection={handleClearSelection}
            userLocation={userLocation}
            onUserLocationChange={handleUserLocationChange}
            isFullscreen={isMapFullscreen}
            onToggleFullscreen={() => setIsMapFullscreen(false)}
          />
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedShelter && (
        <ShelterDetail
          shelter={selectedShelter}
          onClose={handleCloseDetail}
          source={selectedShelterSource}
        />
      )}

      <div ref={footerRef}>
        <Footer />
      </div>
    </div>
  );
};

export default Index;
